import { memo } from 'react';
import { Badge, Button, Table } from 'react-bootstrap';

import Chip from '../../../components/Chip';
import { newRewardRow, PRA_MAX_MILESTONES, PraRewardRow } from '../utils';

type RowProps = {
  row: PraRewardRow;
  index: number;
  canEditStructure: boolean;
  onEditReward: (index: number) => void;
  onRemoveRow: (index: number) => void;
};

const MilestoneRow = memo(function MilestoneRow({
  row,
  index,
  canEditStructure,
  onEditReward,
  onRemoveRow,
}: RowProps): JSX.Element {
  return (
    <tr>
      <td className="align-middle text-center">{index + 1}</td>
      <td className="align-middle">
        <div className="d-flex justify-content-center align-items-center">
          {row.isRandomBox ? (
            <span title="The Random Box is always the LAST milestone; its contents are configured in the box panel and rolled server-side at claim.">
              <Badge variant="dark">
                <i className="fas fa-lock mr-1" />
                Random Box
              </Badge>
            </span>
          ) : (
            <Chip reward={row.reward ?? undefined} canDelete={false} globalAction={() => onEditReward(index)} />
          )}
        </div>
      </td>
      {canEditStructure && (
        <td className="align-middle text-center">
          {!row.isRandomBox && (
            <Button size="sm" variant="outline-danger" title="Remove milestone" onClick={() => onRemoveRow(index)}>
              <i className="fas fa-trash" />
            </Button>
          )}
        </td>
      )}
    </tr>
  );
});

type Props = {
  rows: PraRewardRow[];
  // CHAINED: ordered list with append/remove (1..31). FREE: fixed at exactly one row (D12).
  canEditStructure: boolean;
  onEditReward: (index: number) => void;
  onChangeRows: (rows: PraRewardRow[]) => void;
};

// Ordered milestone rows modeled on MarathonMilestonesTable — `_clientId` row identity so
// React state follows a row when siblings are removed; all updates build fresh arrays
// (never mutate a row in place).
export function PraMilestonesTable({ rows, canEditStructure, onEditReward, onChangeRows }: Props): JSX.Element {
  const handleRemoveRow = (index: number) => {
    onChangeRows(rows.filter((_, rowIndex) => rowIndex !== index));
  };

  // A new milestone goes before the Random Box when one is present (the box stays LAST).
  const handleAppendRow = () => {
    const boxIndex = rows.findIndex((row) => row.isRandomBox);
    const next = [...rows];
    next.splice(boxIndex === -1 ? rows.length : boxIndex, 0, newRewardRow());
    onChangeRows(next);
  };

  return (
    <>
      <Table bordered responsive hover size="sm">
        <thead className="text-center">
          <tr>
            <th style={{ width: 40 }}>#</th>
            <th>Reward</th>
            {canEditStructure && <th style={{ width: 50 }} />}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr>
              <td colSpan={canEditStructure ? 3 : 2} className="text-center text-muted">
                No milestones yet.
              </td>
            </tr>
          )}
          {rows.map((row, index) => (
            <MilestoneRow
              key={row._clientId}
              row={row}
              index={index}
              canEditStructure={canEditStructure}
              onEditReward={onEditReward}
              onRemoveRow={handleRemoveRow}
            />
          ))}
        </tbody>
      </Table>
      {canEditStructure && (
        <div className="d-flex align-items-center mb-3">
          <Button
            size="sm"
            variant="outline-primary"
            disabled={rows.length >= PRA_MAX_MILESTONES}
            onClick={handleAppendRow}
          >
            <i className="fas fa-plus mr-1" />
            Add milestone
          </Button>
          <span className="text-muted small ml-2">
            {rows.length}/{PRA_MAX_MILESTONES} milestones (bitmask cap)
          </span>
        </div>
      )}
    </>
  );
}
